import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PortalLayout from './PortalLayout';
import PortalPageHeader from './PortalPageHeader';
import PortalStatusPill from './PortalStatusPill';
import QuickViewDrawer from '../sub-components/QuickViewDrawer';
import { portalCardSx, statusColor } from './portalStyles';
import PortalContractService from '../../services/PortalContractService';

const STATUS_LABELS = {
    SIGNING: 'Awaiting Signature',
    CHANGES_REQUESTED: 'Changes Requested',
    ACTIVE: 'Active',
    TERMINATED: 'Terminated',
};

function formatDate(d) {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatAmount(val) {
    if (val == null) return '—';
    return 'CHF ' + Number(val).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function PortalContractsPage() {
    const navigate = useNavigate();
    const [contracts, setContracts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [quickView, setQuickView] = useState(null);

    useEffect(() => {
        PortalContractService.getContracts()
            .then(data => setContracts(data || []))
            .catch(() => setError('Failed to load contracts. Please try again.'))
            .finally(() => setLoading(false));
    }, []);

    return (
        <PortalLayout>
            <PortalPageHeader title="Contracts" subtitle="Your contracts and their current status" />

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                    <CircularProgress sx={{ color: '#427898' }} />
                </Box>
            )}

            {!loading && error && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
            )}

            {!loading && !error && contracts.length === 0 && (
                <Paper elevation={0} sx={{ ...portalCardSx, p: 6, textAlign: 'center' }}>
                    <DescriptionOutlinedIcon sx={{ fontSize: 48, color: '#c4cdd6', mb: 2 }} />
                    <Typography variant="body1" sx={{ color: '#94a3b8' }}>
                        No contracts yet.
                    </Typography>
                </Paper>
            )}

            {!loading && !error && contracts.length > 0 && (
                <TableContainer component={Paper} elevation={0} sx={portalCardSx}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Start date</TableCell>
                                <TableCell>End date</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {contracts.map((contract) => (
                                <TableRow
                                    key={contract.id}
                                    hover
                                    sx={{ cursor: 'pointer', '&:last-child td': { border: 0 } }}
                                    onClick={() => setQuickView(contract)}
                                >
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600} sx={{ color: '#1a2332' }}>
                                            {contract.displayName}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <PortalStatusPill status={contract.status} label={STATUS_LABELS[contract.status]} />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: '#475569' }}>
                                            {formatDate(contract.startDate)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: '#475569' }}>
                                            {formatDate(contract.endDate)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600} sx={{ color: '#1a2332' }}>
                                            {formatAmount(contract.totalValue)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <ChevronRightIcon sx={{ color: '#cbd5e1', display: 'block' }} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Quick-view drawer — PDF preview + key facts */}
            <QuickViewDrawer
                open={Boolean(quickView)}
                onClose={() => setQuickView(null)}
                title={quickView?.displayName}
                statusColor={statusColor(quickView?.status)}
                statusLabel={quickView ? (STATUS_LABELS[quickView.status] || quickView.status) : null}
                facts={quickView ? [
                    { label: 'Start date', value: formatDate(quickView.startDate) },
                    { label: 'End date', value: formatDate(quickView.endDate) },
                    { label: 'Amount', value: formatAmount(quickView.totalValue) },
                ] : []}
                loadPdf={() => PortalContractService.downloadPdf(quickView.id)}
                onOpenFull={() => navigate(`/portal/contracts/${quickView.id}`)}
                openLabel="Open contract"
                pdfErrorLabel="PDF preview not available"
            />
        </PortalLayout>
    );
}
